// /src/pages/security-roles/RoleFormPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  InputGroup,
  Badge,
  Container,
} from "react-bootstrap";
import {
  ShieldCheck,
  Search,
  CheckSquare,
  Square,
  X,
  Save,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// UI utilidades (mismas que usas en otros forms)
import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";

// Servicios
import {
  getRoleById,
  createRole,
  updateRole,
} from "../../services/securityRoleService";
import { getAllPermissions } from "../../services/securityPermissionService";

/* =========================================================================
   Helpers
   ======================================================================= */

// Normalizador defensivo para evitar errores si algún campo viene null/undefined
const S = (v) => (v == null ? "" : String(v));

const normalizePerm = (p) => ({
  id: p?.id ?? "",
  key: S(p?.key),
  description: S(p?.description),
  module: S(p?.module) || S(p?.key?.split(".")[0]),
  action: S(p?.action) || S(p?.key?.split(".")[1]),
  isActive: Boolean(p?.isActive),
});

/* =========================================================================
   Validación estilo CompanyForm (Yup + mensajes en español)
   ======================================================================= */

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .required("El nombre es obligatorio")
    .min(3, "Debe tener al menos 3 caracteres")
    .max(100, "No puede exceder los 100 caracteres")
    .test(
      "no-leading-trailing-spaces",
      "El nombre no debe tener espacios al inicio o al final.",
      (value) => value?.trim() === value
    ),
  description: yup
    .string()
    .required("La descripción es obligatoria")
    .min(4, "Debe tener al menos 4 caracteres")
    .max(250, "La descripción no puede exceder los 250 caracteres")
    .test(
      "no-leading-trailing-spaces",
      "La descripción no debe tener espacios al inicio o al final.",
      (value) => value?.trim() === value
    ),
  // validamos por IDs (no por keys)
  permissionIds: yup
    .array()
    .of(yup.string())
    .min(1, "Debe seleccionar al menos un permiso"),
});

/* =========================================================================
   Componente
   ======================================================================= */

const RoleFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // React Hook Form (RHF)
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    watch,
    trigger,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: "", description: "", permissionIds: [] },
  });

  // Estado de UI

  const [catalog, setCatalog] = useState([]); // catálogo de permisos normalizados (array de objetos)
  const [selectedIds, setSelectedIds] = useState([]); // IDs seleccionados
  const [filter, setFilter] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);

  // Errores y éxito (mismo patrón que CompanyForm)
  const [submitErrors, setSubmitErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  // Mantener RHF sincronizado con los IDs seleccionados
  useEffect(() => {
    setValue("permissionIds", selectedIds, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedIds, setValue]);

  // Redirección tras éxito (igual que CompanyForm)
  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => {
        navigate("/dashboard/security-role");
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [successMessage, navigate]);

  /* ---------------------------------------------------------------------
     Carga inicial:
     - Trae catálogo de permisos
     - Si es edición, trae rol y:
       * usa permissionIds si vienen del backend
       * si solo vienen keys, convierte keys -> ids usando el catálogo
  --------------------------------------------------------------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [catResp, roleResp] = await Promise.all([
          getAllPermissions(),
          isEdit
            ? getRoleById(id)
            : Promise.resolve({ success: true, data: null }),
        ]);

        if (!mounted) return;

        // Catálogo de permisos
        if (!catResp?.success) {
          throw new Error(
            catResp?.message || "No se pudo cargar el catálogo de permisos."
          );
        }
        const list = Array.isArray(catResp.data) ? catResp.data : [];
        const normalized = list.map(normalizePerm);
        setCatalog(normalized);

        // Si estamos editando, setear valores iniciales y resolver IDs
        if (isEdit) {
          if (roleResp?.success && roleResp.data) {
            const r = roleResp.data;

            // Preferimos los IDs si el backend los trae
            let initialIds = Array.isArray(r.permissionIds)
              ? r.permissionIds.filter(Boolean)
              : [];

            // Fallback: si no hay IDs pero sí keys, mapear keys -> ids
            if (initialIds.length === 0 && Array.isArray(r.permissionKeys)) {
              const keyToId = new Map(normalized.map((p) => [p.key, p.id]));
              initialIds = r.permissionKeys
                .map((k) => keyToId.get(k))
                .filter(Boolean);
            }

            reset({
              name: r.name ?? "",
              description: r.description ?? "",
              permissionIds: initialIds,
            });
            setSelectedIds(initialIds);
          } else {
            throw new Error("No se pudo cargar la información del rol.");
          }
        }
      } catch (e) {
        // Si la carga falla, mostramos un único error "friendly"
        setSubmitErrors([e.message || "No se pudo cargar la información."]);
      } finally {
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, isEdit, reset]);

  /* ---------------------------------------------------------------------
     Agrupar + filtrar permisos (por módulo), con sort seguro por locale
  --------------------------------------------------------------------- */
  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = Array.isArray(catalog) ? catalog : [];

    const filtered = base.filter((p) => {
      if (onlyActive && !p.isActive) return false;
      if (!q) return true;
      const haystack = (
        p.key +
        " " +
        p.description +
        " " +
        p.module +
        " " +
        p.action
      ).toLowerCase();
      return haystack.includes(q);
    });

    // module -> items[]
    const map = new Map();
    filtered.forEach((perm) => {
      const mod = perm.module || "otros";
      if (!map.has(mod)) map.set(mod, []);
      map.get(mod).push(perm);
    });

    return Array.from(map.entries())
      .sort(([ma], [mb]) =>
        S(ma).localeCompare(S(mb), "es", { sensitivity: "base" })
      )
      .map(([module, items]) => ({
        module,
        items: items
          .slice()
          .sort((a, b) =>
            S(a.key).localeCompare(S(b.key), "es", { sensitivity: "base" })
          ),
      }));
  }, [catalog, filter, onlyActive]);

  /* ---------------------------------------------------------------------
     Selección por ID + estilo visual al estar seleccionado
  --------------------------------------------------------------------- */
  const isChecked = (permId) => selectedIds.includes(permId);

  const toggleOne = (permId) =>
    setSelectedIds((prev) =>
      prev.includes(permId)
        ? prev.filter((x) => x !== permId)
        : [...prev, permId]
    );

  const moduleStats = (items) => {
    const ids = items.map((p) => p.id);
    const count = ids.length;
    const checked = ids.filter((id) => selectedIds.includes(id)).length;
    return {
      count,
      checked,
      all: checked === count,
      some: checked > 0 && checked < count,
    };
  };

  const toggleModule = (items) => {
    const ids = items.map((p) => p.id);
    const { all } = moduleStats(items);
    setSelectedIds((prev) => {
      if (all) {
        // Deseleccionar todos los del módulo
        const remove = new Set(ids);
        return prev.filter((id) => !remove.has(id));
      }
      // Seleccionar todos (evitando duplicados)
      return Array.from(new Set([...prev, ...ids]));
    });
  };

  /* ---------------------------------------------------------------------
     Submit: envía exactamente { name, description, permissionIds }
     Captura errores con extractApiErrors y muestra AlertMessage
  --------------------------------------------------------------------- */
  const onSubmit = async (values) => {
    setSubmitErrors([]);
    setSuccessMessage(null);

    try {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        permissionIds: values.permissionIds, // IDs listos desde RHF
      };

      const resp = isEdit
        ? await updateRole({ id, ...payload }) // tu servicio updateRole espera un sólo objeto con id
        : await createRole(payload);

      if (!resp?.success) {
        // Forzamos el mismo flujo de errores que CompanyForm
        throw { backend: resp };
      }

      setSuccessMessage(
        `✅ Rol ${isEdit ? "actualizado" : "creado"} correctamente.`
      );
    } catch (error) {
      const parsed = extractApiErrors(error);
      setSubmitErrors(parsed);
    }
  };

  const permissionIdsWatch = watch("permissionIds") || [];

  /* ---------------------------------------------------------------------
     Render
  --------------------------------------------------------------------- */
  return (
    <Container className="mt-4">
      <Card className="p-4 novian-form">
        {/* Header con icono */}
        <div className="d-flex align-items-center mb-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle me-3 text-dark"
            style={{ width: 44, height: 44, background: "rgba(13,110,253,.1)" }}
          >
            <ShieldCheck size={20} />
          </div>
          <div>
            <h5 className="mb-0 text-dark">
              {isEdit ? "Editar rol" : "Crear rol"}
            </h5>
            <small className=" text-dark">
              Define nombre, descripción y asigna los permisos correspondientes.
            </small>
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Row className="g-3">
            {/* Nombre */}
            <Col md={6}>
              <Form.Group controlId="roleName">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="p. ej., Abogado nivel 1"
                  isInvalid={!!errors.name}
                  {...register("name")}
                  onBlur={() => trigger("name")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Descripción */}
            <Col md={6}>
              <Form.Group controlId="roleDesc">
                <Form.Label>Descripción *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Breve descripción del rol"
                  isInvalid={!!errors.description}
                  {...register("description")}
                  onBlur={() => trigger("description")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Selector de permisos */}
            <Col xs={12}>
              <Form.Group>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Form.Label className="mb-0">Permisos</Form.Label>
                  <div className="d-flex align-items-center gap-3">
                    <Form.Check
                      type="switch"
                      id="only-active"
                      label="Solo activos"
                      checked={onlyActive}
                      onChange={(e) => setOnlyActive(e.target.checked)}
                    />
                    <Badge bg="light" text="dark">
                      {permissionIdsWatch.length} seleccionados
                    </Badge>
                  </div>
                </div>

                {/* Buscador */}
                <InputGroup className="mb-2" role="search">
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Buscar por key, descripción, módulo o acción"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </InputGroup>

                {/* Lista agrupada con scroll vertical */}
                <div
                  className="border rounded-3 p-2"
                  style={{ maxHeight: "50vh", overflowY: "auto" }}
                >
                  {grouped.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      No hay permisos que coincidan con la búsqueda.
                    </div>
                  ) : (
                    grouped.map(({ module, items }) => {
                      const stats = moduleStats(items);
                      return (
                        <div key={module} className="mb-2">
                          {/* Cabecera de módulo con seleccionar/deseleccionar todo */}
                          <button
                            type="button"
                            onClick={() => {
                              toggleModule(items);
                              // Dispara validación del campo permissionIds
                              setTimeout(() => trigger("permissionIds"), 0);
                            }}
                            className="btn btn-sm d-inline-flex align-items-center mb-1 text-dark"
                            style={{ gap: 8 }}
                          >
                            {stats.all ? (
                              <CheckSquare size={16} />
                            ) : (
                              <Square size={16} />
                            )}
                            <strong className="me-2 text-dark">{module}</strong>
                            <Badge
                              bg={
                                stats.all
                                  ? "success"
                                  : stats.some
                                  ? "warning"
                                  : "secondary"
                              }
                            >
                              {stats.checked}/{stats.count}
                            </Badge>
                          </button>

                          {/* Items del módulo */}
                          <div className="row row-cols-1 row-cols-md-2 g-1">
                            {items.map((perm) => {
                              const checked = isChecked(perm.id);
                              return (
                                <div key={perm.id} className="col">
                                  <label
                                    className="d-flex align-items-center gap-2 border rounded-2 px-2 py-1 text-dark"
                                    style={{
                                      //Resaltado visual cuando está seleccionado
                                      backgroundColor: checked
                                        ? "rgba(13,110,253,0.08)"
                                        : undefined,
                                      borderColor: checked
                                        ? "#0d6efd"
                                        : undefined,
                                      boxShadow: checked
                                        ? "0 0 0 2px rgba(13,110,253,0.25)"
                                        : undefined,
                                      transition: "all 120ms ease-in-out",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      className="form-check-input m-0 text-dark"
                                      checked={checked}
                                      onChange={() => {
                                        toggleOne(perm.id);
                                        setTimeout(
                                          () => trigger("permissionIds"),
                                          0
                                        );
                                      }}
                                    />
                                    <div className="d-flex flex-column text-dark">
                                      <code className="small mb-0">
                                        {perm.key}
                                      </code>
                                      {perm.description && (
                                        <small
                                          className="text-dark"
                                          title={perm.description}
                                        >
                                          {perm.description}
                                        </small>
                                      )}
                                    </div>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Error del bloque de permisos */}
                {errors.permissionIds && (
                  <div className="text-danger small mt-2">
                    {errors.permissionIds.message}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Alertas de backend (mismo patrón que CompanyForm) */}
          {submitErrors.length > 0 && (
            <AlertMessage
              type="error"
              message={
                <ul className="mb-0">
                  {submitErrors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              }
              onClose={() => setSubmitErrors([])}
            />
          )}

          {successMessage && (
            <AlertMessage
              type="success"
              message={successMessage}
              onClose={() => setSuccessMessage(null)}
            />
          )}

          {/* Botones */}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => navigate("/dashboard/security-role")}
              disabled={isSubmitting}
            >
              <X size={16} className="me-1" />
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Guardando…
                </>
              ) : (
                <>
                  <Save size={16} className="me-1" /> Guardar
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
};

export default RoleFormPage;
